import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Conversation from 'components/common/Conversation';
import React, { useEffect, useState } from 'react';
import { useGetRecommendedClientsMutation } from 'services/api/cars.api';
import priceFormat from 'services/format/priceFormat';
import type { Client } from 'services/types/client';
import { SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface RecommendClientsModalProps {
  open: boolean;
  onClose: () => void;
  carId: number;
}

const LIMIT = 500;

const RecommendClientsModal: React.FC<RecommendClientsModalProps> = ({ open, onClose, carId }) => {
  const [expandedClient, setExpandedClient] = useState<number | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [analyzedClientsCount, setAnalyzedClientsCount] = useState<number>(0);

  const [getRecommendedClients, { data, isLoading }] = useGetRecommendedClientsMutation();

  const fetchRecommendedClients = () => {
    getRecommendedClients({ carId, aiAnalysisLimit: LIMIT, pageIndex: analyzedClientsCount / LIMIT }).catch((error) => {
      console.error('Error fetching clients', error);
    });
  };

  useEffect(() => {
    setAnalyzedClientsCount(0);
    setClients([]);
    fetchRecommendedClients();
  }, [carId]);

  useEffect(() => {
    if (data) {
      setAnalyzedClientsCount(analyzedClientsCount + data.analyzedClientsCount);
      setClients([...clients, ...data.recommendedClients]);
    }
  }, [data]);

  const handleOpenClientDetails = (clientId: number) => {
    window.open(`/clients/${clientId}`, '_blank');
  };

  const toggleExpandClient = (clientId: number) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Recomendar Auto a Clientes</DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        {/* Car info card */}
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Buscar clientes que podrían estar interesados en:
          </Typography>
          <Typography variant="h6">
            {data?.car.brand} {data?.car.model} {data?.car.version} ({data?.car.year}) -{' '}
            {priceFormat.format(data?.car.price || 0)}
          </Typography>
        </Box>

        {analyzedClientsCount > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              {`${analyzedClientsCount} clientes analizados, ${clients.length} seleccionados`}
            </Typography>

            {/* Client list as card rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {clients.map((client, index) => (
                <React.Fragment key={client.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: index % 2 === 0 ? SURFACE_CONTAINER_LOWEST : SURFACE_CONTAINER_LOW,
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpenClientDetails(client.id)}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {client.firstName ? `${client.firstName} ${client.lastName}` : `Cliente ${client.id}`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Último mensaje: {new Date(client.lastMessageDate).toLocaleDateString('es-CL')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenClientDetails(client.id);
                        }}
                      >
                        Ver cliente
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpandClient(client.id);
                        }}
                        endIcon={expandedClient === client.id ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />}
                      >
                        Ver mensajes
                      </Button>
                    </Box>
                  </Box>
                  <Collapse in={expandedClient === client.id} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 2, py: 2, backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: 2, mb: 0.5 }}>
                      {client.messages && client.messages.length > 0 ? (
                        <Conversation
                          conversation={client.messages}
                          isFilteredConversation={false}
                          fullHeight={false}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No hay mensajes disponibles
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </Box>
          </>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="contained" color="secondary" onClick={fetchRecommendedClients} disabled={isLoading}>
            Buscar más clientes
          </Button>
        </Box>
        {data && data.analyzedClientsCount === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No hay más clientes para analizar
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecommendClientsModal;
